// 'use strict';

// define(['underscore', 'three', 'd3'], function (_, THREE, d3) {

import _values from 'lodash/values'
import _groupBy from 'lodash/groupBy'
import _flatten from 'lodash/flatten'
import _range from 'lodash/range'
import _defaults from 'lodash/defaults'
import * as THREE from 'three'
import * as d3 from 'd3'

const tubularSegments = 50
const radiusSegments = 8
const // 4,
    ballRadius = 2
const // 2.5,
    ballGeom = new THREE.SphereBufferGeometry(ballRadius, 16, 8)

/**
 * Naive first stab at tapering the heads of hit tubes to points.
 * Modifies the first set of radius segment points on the tube curve by averaging their
 * coordinates to a common center point. Should be the same as the lead point in the
 * curve fed to THREE.TubeBufferGeometry.
 * Later, can generalize to more gradually taper by interpolating between joint center points and
 * joint radius segment points.
 * @param geom
 */
function taperTube(geom) {
    const buff = geom.attributes.position.array
    const coords = _values(_groupBy(buff, (coord, i) => Math.floor(i / 3)))
    const joints = _values(
        _groupBy(coords, (point, i) => Math.floor(i / (radiusSegments + 1)))
    )
    const firstJoint = joints[0]
    const xCoords = firstJoint.map((coord) => coord[0])
    const yCoords = firstJoint.map((coord) => coord[1])
    const zCoords = firstJoint.map((coord) => coord[2])
    const x = d3.sum(xCoords) / xCoords.length
    const y = d3.sum(yCoords) / yCoords.length
    const z = d3.sum(zCoords) / zCoords.length
    const newJoint = _flatten(_range(xCoords.length).map(() => [x, y, z]))

    newJoint.forEach((n, i) => {
        buff[i] = n
    })
}

function Hit(data, opts, metadata) {
    THREE.Group.call(this)

    opts = _defaults(opts || {}, {
        color: 0xcccccc,
        ballColor: 0xf8f6e4,
        opacity: 0.1,
        drawTo: 0, // Infinity,
        meta: {},
    })

    /* trail */
    const pos = new Float32Array(data.path)
    const posLen = pos.length / 3
    const arr = []

    for (let i = 0; i < posLen; i++) {
        const v = pos.subarray(3 * i, 3 * i + 3)
        arr.push(new THREE.Vector3(v[0], v[1], v[2]))
    }

    const curve = new THREE.CatmullRomCurve3(arr)
    const radius = 0.8
    const geom = new THREE.TubeBufferGeometry(
        curve,
        tubularSegments,
        radius,
        radiusSegments,
        false
    )
    // isTransparent = opts.opacity < 1,
    const mat = new THREE.MeshPhongMaterial({
        color: opts.color,
        opacity: opts.opacity,
        transparent: true, // isTransparent,
        flatShading: THREE.SmoothShading, // THREE.FlatShading,
        shininess: 50,
        // side: isTransparent ? THREE.FrontSide: THREE.DoubleSide,
        side: THREE.FrontSide,
        depthWrite: true,
        // alphaTest: 0.5
    })

    taperTube(geom)

    // why * 6? might be 6 verts per segment side, at 2 triangles per side
    this.segmentStride = radiusSegments * 6
    this.scaleSegment = d3
        .scaleQuantize()
        .domain([0, 1])
        .range(d3.range(tubularSegments + 0.5))

    const path = new THREE.Mesh(geom, mat)
    path.name = 'path'
    path.geometry.setDrawRange(0, opts.drawTo)
    // path.castShadow = true;
    path.userData = metadata

    this.add(path)

    /* ball */
    const ballMat = new THREE.MeshPhongMaterial({
        color: opts.ballColor,
        flatShading: THREE.SmoothShading, // THREE.FlatShading,
        shininess: 40,
        transparent: true, // false,
        // opacity: Math.min(2 * opts.color, 1),
        opacity: 0.8,
        depthWrite: true,
    })
    const ball = new THREE.Mesh(ballGeom, ballMat)

    ball.name = 'ball'
    ball.visible = false
    ball.userData = metadata

    // TODOHI  need to position ball here?

    this.add(ball)

    // create state obj
    this.props = {
        defaultColor: new THREE.Color(opts.color),
        defaultOpacity: opts.opacity,
        defaultDrawTo: opts.drawTo,
        meta: opts.meta,
    }

    this.state = {
        range: {
            in: 0,
            out: 0,
        },
        color: opts.color,
        drawTo: opts.drawTo,
        opacity: opts.opacity,
    }

    this.userData = metadata
}

Hit.prototype = Object.create(THREE.Group.prototype)
Hit.prototype.constructor = Hit

// note: 'from' argument unused so far; first arg to setDrawRange always 0
Hit.prototype.show = function (from, to) {
    const path = this.getObjectByName('path')
    const pathGeom = path.geometry
    const pathCurve = pathGeom.parameters.path

    /* trail */
    // https://stackoverflow.com/questions/36426139/incrementally-display-three-js-tubegeometry/36439563#36439563
    const segments = this.scaleSegment(to)
    const drawTo = segments === 0 ? 0 : (segments + 1) * this.segmentStride

    // pathGeom.setDrawRange(0, drawTo);

    path.visible = to > 0
    if (to > 0) {
        pathGeom.setDrawRange(0, drawTo)
    }

    /* ball */
    // var head = pathCurve.getPointAt(Math.min(to + 0.05, 1)),
    const head = pathCurve.getPointAt(Math.min(to + 0.025, 1))
    const ball = this.getObjectByName('ball')

    // TODOHI  if to === 0, hide ball?

    ball.visible = to > 0

    ball.position.copy(head)

    // update state
    this.state.drawTo = drawTo
    this.state.range.in = from
    this.state.range.out = to
}

Hit.prototype.color = function (color, opacity) {
    this.state.opacity = opacity || this.state.opacity
    this.state.color = color || this.state.color

    const path = this.getObjectByName('path')
    path.material.color = new THREE.Color(this.state.color)
    path.material.opacity = this.state.opacity

    const ball = this.getObjectByName('ball')
    ball.material.color = new THREE.Color(this.state.color)
    ball.material.opacity = Math.min(2 * this.state.opacity, 1)
}

Hit.prototype.resetColor = function () {
    const path = this.getObjectByName('path')
    path.material.color = this.props.defaultColor
    path.material.opacity = this.props.defaultOpacity

    const ball = this.getObjectByName('ball')
    ball.material.color = this.props.defaultColor
    ball.material.opacity = Math.min(2 * this.props.defaultOpacity, 1)
}

Hit.fromData = function (play, opts) {
    const tracking = play.result.hit
    const hit = new Hit(tracking, opts, play)

    hit.name = play.id
    hit.userData = play

    return hit
}

export default Hit
